'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { cn } from "@/lib/utils";
import { ConditionParamsEditor } from './ConditionParamsEditor';
import type {
  ConditionDefinition,
  ConditionValue,
  ConditionBuilderRule,
  ConditionBuilderMode,
  ConditionParams,
  isCompoundCondition,
  isLegacyCondition,
  isConditionObject,
} from '../types/conditions';
import { getValidCategoriesForSource, type ConditionCategory } from '../constants/edgeConditions';

interface ConditionBuilderProps {
  availableConditions: ConditionDefinition[];
  value: ConditionValue;
  onChange: (value: ConditionValue) => void;
  storeId: string;
  sourceNodeType?: string;
}

/**
 * Filter conditions based on source node type using centralized category rules.
 */
function filterConditionsBySourceType(
  conditions: ConditionDefinition[],
  sourceType?: string
): ConditionDefinition[] {
  if (!sourceType) return conditions;
  const validCategories = getValidCategoriesForSource(sourceType);
  return conditions.filter((c) => validCategories.includes(c.category as ConditionCategory));
}

/**
 * Compact AND/OR Condition Builder
 */
export function ConditionBuilder({ availableConditions, value, onChange, storeId, sourceNodeType }: ConditionBuilderProps) {
  const { t } = useTranslation('storefront');
  const [mode, setMode] = React.useState<ConditionBuilderMode>('single');
  const [rules, setRules] = React.useState<ConditionBuilderRule[]>([]);

  // Track the last external value to detect when it changes from outside
  const lastExternalValueRef = React.useRef<string | null>(null);

  // Helper to serialize value for comparison (stable function)
  const serializeValue = React.useCallback((v: ConditionValue): string => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    return JSON.stringify(v);
  }, []);

  // Parse array of conditions into rules
  const parseRulesArray = React.useCallback((arr: ConditionValue[]): ConditionBuilderRule[] => {
    return arr.map((item) => {
      if (typeof item === 'string') {
        return { id: crypto.randomUUID(), conditionName: item, params: {} };
      }
      if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
        const keys = Object.keys(item);
        if (keys.length === 1) {
          const conditionName = keys[0];
          const paramValue = (item as Record<string, unknown>)[conditionName];
          const params =
            typeof paramValue === 'object' && paramValue !== null ? (paramValue as ConditionParams) : {};
          return { id: crypto.randomUUID(), conditionName, params };
        }
      }
      return { id: crypto.randomUUID(), conditionName: '', params: {} };
    });
  }, []);

  // Initialize/reset from value when it changes externally
  React.useEffect(() => {
    const serialized = serializeValue(value);

    // Skip if value hasn't changed from external source
    if (lastExternalValueRef.current === serialized) {
      return;
    }

    // Update ref
    lastExternalValueRef.current = serialized;

    // Parse and set state
    if (!value) {
      setMode('single');
      setRules([{ id: crypto.randomUUID(), conditionName: '', params: {} }]);
    } else if (typeof value === 'string') {
      setMode('single');
      setRules([{ id: crypto.randomUUID(), conditionName: value, params: {} }]);
    } else if (typeof value === 'object' && value !== null) {
      const keys = Object.keys(value);

      if (keys.includes('and') && 'and' in value && Array.isArray(value.and)) {
        setMode('and');
        setRules(parseRulesArray(value.and));
      } else if (keys.includes('or') && 'or' in value && Array.isArray(value.or)) {
        setMode('or');
        setRules(parseRulesArray(value.or));
      } else if (keys.length === 1) {
        const conditionName = keys[0];
        const paramValue = (value as Record<string, unknown>)[conditionName];
        const params =
          typeof paramValue === 'object' && paramValue !== null && !Array.isArray(paramValue)
            ? (paramValue as ConditionParams)
            : {};
        setMode('single');
        setRules([{ id: crypto.randomUUID(), conditionName, params }]);
      }
    }
  }, [value, serializeValue, parseRulesArray]); // ✅ Added missing dependencies

  const emitChange = React.useCallback(
    (newMode: ConditionBuilderMode, newRules: ConditionBuilderRule[]) => {
      const validRules = newRules.filter((r) => r.conditionName);

      if (validRules.length === 0) {
        // Update ref before calling onChange to prevent re-parse
        lastExternalValueRef.current = '';
        onChange(undefined);
        return;
      }

      const buildCondition = (rule: ConditionBuilderRule): Record<string, ConditionParams | boolean> => {
        const hasParams = Object.keys(rule.params).some(
          (k) => rule.params[k] !== '' && rule.params[k] !== undefined,
        );
        if (hasParams) {
          return { [rule.conditionName]: rule.params };
        }
        return { [rule.conditionName]: true };
      };

      let newValue: ConditionValue;
      if (newMode === 'single' || validRules.length === 1) {
        const rule = validRules[0];
        const hasParams = Object.keys(rule.params).some(
          (k) => rule.params[k] !== '' && rule.params[k] !== undefined,
        );
        if (hasParams) {
          newValue = { [rule.conditionName]: rule.params };
        } else {
          newValue = rule.conditionName;
        }
      } else {
        const conditions = validRules.map(buildCondition);
        newValue = { [newMode]: conditions };
      }

      // Update ref before calling onChange to prevent re-parse
      lastExternalValueRef.current = serializeValue(newValue);
      onChange(newValue);
    },
    [onChange, serializeValue],
  );

  const handleModeChange = (newMode: ConditionBuilderMode) => {
    setMode(newMode);
    let newRules = [...rules];
    if ((newMode === 'and' || newMode === 'or') && rules.length < 2) {
      newRules = [...rules, { id: crypto.randomUUID(), conditionName: '', params: {} }];
      setRules(newRules);
    }
    emitChange(newMode, newRules);
  };

  const addRule = () => {
    const newRules = [...rules, { id: crypto.randomUUID(), conditionName: '', params: {} }];
    setRules(newRules);
    // Auto-switch to AND if adding second condition while in single mode
    const newMode: ConditionBuilderMode = rules.length === 1 && mode === 'single' ? 'and' : mode;
    if (newMode !== mode) setMode(newMode);
    emitChange(newMode, newRules);
  };

  const removeRule = (id: string) => {
    const newRules = rules.filter((r) => r.id !== id);
    if (newRules.length === 0) {
      newRules.push({ id: crypto.randomUUID(), conditionName: '', params: {} });
    }
    setRules(newRules);
    const newMode: ConditionBuilderMode = newRules.length === 1 ? 'single' : mode;
    setMode(newMode);
    emitChange(newMode, newRules);
  };

  const updateRuleCondition = (id: string, conditionName: string) => {
    const newRules = rules.map((r) => (r.id === id ? { ...r, conditionName, params: {} } : r));
    setRules(newRules);
    emitChange(mode, newRules);
  };

  const updateRuleParams = (id: string, params: ConditionParams) => {
    const newRules = rules.map((r) => (r.id === id ? { ...r, params } : r));
    setRules(newRules);
    emitChange(mode, newRules);
  };

  const getConditionDef = (conditionName: string): ConditionDefinition | undefined => {
    return availableConditions.find((c) => c.name === conditionName);
  };

  const formatConditionName = (name: string) => {
    return name
      .split('.')
      .map((part) =>
        part
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim(),
      )
      .join(' • ');
  };

  // Filter conditions based on source node type, then group by category
  const groupedConditions = React.useMemo(() => {
    const filteredConditions = filterConditionsBySourceType(availableConditions, sourceNodeType);
    return filteredConditions.reduce(
      (acc, c) => {
        if (!acc[c.category]) acc[c.category] = [];
        acc[c.category].push(c);
        return acc;
      },
      {} as Record<string, ConditionDefinition[]>,
    );
  }, [availableConditions, sourceNodeType]);

  return (
    <div className="w-full min-w-0 space-y-2 overflow-hidden">
      {/* Rules */}
      {rules.map((rule, index) => (
        <div key={rule.id} className="w-full min-w-0">
          {/* AND/OR Connector between rules */}
          {index > 0 && (
            <div className="flex items-center gap-2 py-1.5">
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              <div className="flex shrink-0 items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-800">
                <button
                  type="button"
                  onClick={() => handleModeChange('and')}
                  className={cn(
                    'rounded px-2 py-0.5 text-xs font-semibold transition-colors',
                    mode === 'and'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300',
                  )}
                >
                  AND
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('or')}
                  className={cn(
                    'rounded px-2 py-0.5 text-xs font-semibold transition-colors',
                    mode === 'or'
                      ? 'bg-amber-500 text-white'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300',
                  )}
                >
                  OR
                </button>
              </div>
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
            </div>
          )}

          {/* Rule Row */}
          <div className="flex w-full min-w-0 items-start gap-2">
            <div className="min-w-0 flex-1">
              <ConditionSelector
                value={rule.conditionName}
                onChange={(name) => updateRuleCondition(rule.id, name)}
                groupedConditions={groupedConditions}
                formatConditionName={formatConditionName}
              />

              {/* Inline Params */}
              {rule.conditionName && getConditionDef(rule.conditionName)?.params && (
                <div className="mt-2 w-full min-w-0">
                  <ConditionParamsEditor
                    params={getConditionDef(rule.conditionName)!.params!}
                    values={rule.params}
                    storeId={storeId}
                    onChange={(params) => updateRuleParams(rule.id, params)}
                  />
                </div>
              )}
            </div>

            {/* Remove Button */}
            {rules.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 text-slate-400 hover:text-red-500"
                onClick={() => removeRule(rule.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {/* Add Condition */}
      <Button
        variant="ghost"
        size="sm"
        className="w-full min-w-0 truncate text-slate-500 hover:text-slate-700 dark:text-slate-400"
        onClick={addRule}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{rules.length === 1 ? t('conditionBuilder.addConditionAndOr') : t('conditionBuilder.addCondition')}</span>
      </Button>
    </div>
  );
}

/**
 * Condition Selector Dropdown
 */
interface ConditionSelectorProps {
  value: string;
  onChange: (name: string) => void;
  groupedConditions: Record<string, ConditionDefinition[]>;
  formatConditionName: (name: string) => string;
}

function ConditionSelector({
  value,
  onChange,
  groupedConditions,
  formatConditionName,
}: ConditionSelectorProps) {
  const { t } = useTranslation('storefront');
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full min-w-0 justify-between overflow-hidden text-sm font-normal"
        >
          {value ? (
            <span className="min-w-0 truncate">{formatConditionName(value)}</span>
          ) : (
            <span className="text-muted-foreground truncate">{t('conditionBuilder.selectCondition')}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('conditionBuilder.searchConditions')} className="h-9 text-sm" />
          <CommandList>
            <CommandEmpty>{t('conditionBuilder.noConditionFound')}</CommandEmpty>
            {/* No condition option */}
            <CommandGroup>
              <CommandItem
                value="none no condition"
                onSelect={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                <span className="text-slate-500">{t('conditionBuilder.noConditionFallback')}</span>
              </CommandItem>
            </CommandGroup>
            {Object.entries(groupedConditions).map(([category, conditions]) => (
              <CommandGroup key={category} heading={category.charAt(0).toUpperCase() + category.slice(1)}>
                {conditions.map((condition) => (
                  <CommandItem
                    key={condition.name}
                    value={`${condition.name} ${condition.description}`}
                    onSelect={() => {
                      onChange(condition.name);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === condition.name ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {formatConditionName(condition.name)}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        {condition.description}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
